import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ConnectableObservable, Observable, Subject } from 'rxjs';
import { from } from 'rxjs/observable/from';
import { distinctUntilChanged, finalize, map, publishReplay, switchAll, tap } from 'rxjs/operators';
import { prepareDataForAuth } from '../../utils';
/**
 * Questa classe rappresenta una risorsa completamente posseduta da un singolo utente.
 *
 * L'assunzione di base è che un solo utente alla volta può aggiornare il valore di questo
 * oggetto.
 *
 * L'update si comporta in modo parzialmente sincrono: invia subito il nuovo valore,
 * tuttavia in seguito genera un eccezione nel caso in cui non sia riuscito a
 * ottenere una risposta dal server.
 *
 * @export
 */
export class RESTObject<T> extends Observable<T> {

	protected generator: Subject<Observable<T>>;

	// get uri() {
	// 	// nel dubbio inserisco l'auth in ogni richiesta
	// 	if(this._uri.indexOf('__data') === -1) {
	// 		const __data = prepareDataForAuth()
	// 		return `${this._uri}?__data=${__data}`
	// 	}
	// 	return this._uri
	// }

	constructor(
		protected readonly $http: HttpClient,
		protected readonly uri: string,
		defaultValue?: T
	) {
		super();
		this.generator = new Subject<Observable<T>>();

		const source = this.generator.asObservable().pipe(
			switchAll(),
			distinctUntilChanged(),
			publishReplay(1)
		) as ConnectableObservable<T>;


		this.source = source;

		let connected = false;
		const self = this;

		this.operator = function(s) {
			if (!connected) {
				source.connect();
				connected = true;
				// Se mi viene fornito un default, allora non mi serve
				// refreshare, sarà l'utente a decidere quando e se.
				if (defaultValue !== null && defaultValue !== undefined) {
					self.mapRequest(from([ defaultValue ]));
				} else {
					self.load();
				}
			}
			return s.subscribe(this);
		};
	}

	protected mapRequest(req: Observable<T>): Promise<T> {
		// Questo per sapere se la chiamata è andata a buon fine o no!
		return new Promise<T>((resolve, reject) => {
			const obs = req.pipe(
				// Metto la risoluzione in "do", di modo che non avvenga nessuna
				// subscription, ma che venga risolta quando serve.
				tap({
					next: resolve,
					error: (err) => reject(err)
				}),
				// In finally faccio sempre reject. Se la promise è stata risolta
				// in precedenza, questa istruzione non ha effetto!
				// (quindi l'unico caso in cui questa chiamata ha effetto è
				// quando la richiesta viene cancellata)
				finalize(() => reject('Richiesta cancellata!'))
			) as Observable<T>;

			this.generator.next(obs);
		});
	}

	load(options?: {
		headers?: HttpHeaders;
		params?: HttpParams;
		reportProgress?: boolean;
	}): Promise<T> {
		return this.mapRequest(
			this.$http.get<T>(this.uri, options)
		);
	}

	update(newValue: T, options?: {
		headers?: HttpHeaders;
		params?: HttpParams;
		reportProgress?: boolean;
	}): Promise<T> {
		// La update è speciale: non ha senso che restituisca un valore uguale a quello inviato
		// Inviamo subito il risultato per la visualizzazione
		this.mapRequest(from([ newValue ])); // TODO: spinner?

		return this.mapRequest(this.$http.post(this.uri, newValue, {
				responseType: 'text',
				...options
			})
			// Questo ignora il risultato della richiesta (se non per il fatto che è andata
			// a buon fine!)
			.pipe(map(() => newValue))
		);
	}

	reset(body?: any, options?: {
		headers?: HttpHeaders;
		params?: HttpParams;
		reportProgress?: boolean;
	}): Promise<T> {
		return this.mapRequest(
			this.$http.delete<T>(this.uri, { params: body, ...options })
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/angular-rest/RESTObject.ts